import React, { createContext, useContext, useState, useEffect } from 'react';
import { Notification } from '../types';
import {
  isPushNotificationSupported,
  checkNotificationPermission,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getCurrentPushSubscription
} from '../utils/notifications/subscriptionUtils';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../api/client';

// Define notification preferences type
export interface NotificationPreferences {
  enabled: boolean;
  transactionUpdates: boolean;
  securityAlerts: boolean;
  accountUpdates: boolean;
  marketingMessages: boolean;
}

// Define the context type
interface NotificationContextType {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: Error | null;
  requestPermission: () => Promise<NotificationPermission>;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  updateNotificationPreferences: (preferences: NotificationPreferences) => Promise<void>;
  notificationPreferences: NotificationPreferences;
  // Add properties needed for NotificationsScreen
  notifications: Notification[];
  getNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
}

// Default notification preferences
const defaultPreferences: NotificationPreferences = {
  enabled: true,
  transactionUpdates: true,
  securityAlerts: true,
  accountUpdates: true,
  marketingMessages: false
};

// Create the context
export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Provider component
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, currentUser } = useAuth();
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>(defaultPreferences);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Check if push notifications are supported
  useEffect(() => {
    setIsSupported(isPushNotificationSupported());
  }, []);

  // Check notification permission and subscription status
  useEffect(() => {
    const checkStatus = async () => {
      if (!isSupported || !isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Check permission
        const currentPermission = await checkNotificationPermission();
        setPermission(currentPermission);
        
        // Check subscription
        const subscription = await getCurrentPushSubscription();
        setIsSubscribed(!!subscription);
        
        // Get notification preferences
        if (isAuthenticated && currentUser?.id) {
          await fetchNotificationPreferences();
        }
        
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [isSupported, isAuthenticated, currentUser?.id]);

  // Fetch notification preferences from the server
  const fetchNotificationPreferences = async () => {
    try {
      const preferences = await apiClient.notifications.getPreferences();
      setNotificationPreferences(preferences as NotificationPreferences);
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
      // Use default preferences if fetch fails
      setNotificationPreferences(defaultPreferences);
    }
  };

  // Request permission for notifications
  const requestPermission = async (): Promise<NotificationPermission> => {
    try {
      const newPermission = await requestNotificationPermission();
      setPermission(newPermission);
      return newPermission;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to request permission'));
      return 'denied';
    }
  };

  // Subscribe to push notifications
  const subscribe = async (): Promise<boolean> => {
    if (!isSupported) {
      setError(new Error('Push notifications are not supported'));
      return false;
    }

    if (permission !== 'granted') {
      const newPermission = await requestPermission();
      if (newPermission !== 'granted') {
        return false;
      }
    }

    try {
      setIsLoading(true);
      const subscription = await subscribeToPushNotifications();
      
      if (subscription) {
        // Save subscription to server
        await apiClient.notifications.saveSubscription(subscription);
      }
      
      setIsSubscribed(!!subscription);
      setError(null);
      return !!subscription;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to subscribe'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribe = async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    try {
      setIsLoading(true);
      
      // Get current subscription before unsubscribing
      const currentSubscription = await getCurrentPushSubscription();
      
      const success = await unsubscribeFromPushNotifications();
      
      // If we had a subscription and unsubscribe was successful, remove from server
      if (currentSubscription && success) {
        await apiClient.notifications.deleteSubscription(currentSubscription.endpoint);
      }
      
      setIsSubscribed(!success);
      setError(null);
      return success;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to unsubscribe'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Update notification preferences
  const updateNotificationPreferences = async (preferences: NotificationPreferences): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Update on the server
      await apiClient.notifications.updatePreferences(preferences);
      
      // Update local state
      setNotificationPreferences(preferences);
      
      // If notifications are disabled, unsubscribe
      if (!preferences.enabled && isSubscribed) {
        await unsubscribe();
      }
      
      // If notifications are enabled but not subscribed, subscribe
      if (preferences.enabled && !isSubscribed && permission === 'granted') {
        await subscribe();
      }
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update preferences'));
    } finally {
      setIsLoading(false);
    }
  };

  // Get all notifications
  const getNotifications = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const fetchedNotifications = await apiClient.notifications.getAll();
      setNotifications(fetchedNotifications as Notification[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (id: string): Promise<void> => {
    try {
      await apiClient.notifications.markAsRead(id);
      
      // Update the local state to mark the notification as read
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === id
            ? { ...notification, read: true }
            : notification
        )
      );
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to mark notification as read'));
    }
  };

  // Context value
  const value: NotificationContextType = {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    updateNotificationPreferences,
    notificationPreferences,
    notifications,
    getNotifications,
    markAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Note: The useNotifications hook is now defined in src/hooks/useNotifications.ts